/**
 * Dashboard floating-sidebar — sidebar toggle, scroll-spy, smooth-scroll.
 *
 * Three anchor links navigate to the dashboard's section divs (which carry
 * the ids `#economic-outlook-section`, `#expected-returns-section`,
 * `#monetary-policy-section`). The active link is updated on scroll: pick
 * the bottommost section whose top edge has crossed the viewport midline.
 *
 * Visibility timing: the sidebar fades in 300ms after mount so it doesn't
 * pop in alongside the landing overlay (a sibling that fades over 800ms).
 *
 * On viewports < 1024px the sidebar is hidden entirely (CSS rule), since
 * it would overlap the dashboard cards on narrow screens.
 */
import { useEffect, useRef, useState } from 'react';
import { formatLong } from '@/lib/utils/dates';
import styles from './FloatingSidebar.module.css';

const SECTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'economic-outlook-section', label: 'Economic Outlook' },
  { id: 'expected-returns-section', label: 'Forecasts' },
  { id: 'monetary-policy-section', label: 'Monetary Policy' },
];

export default function FloatingSidebar(): JSX.Element {
  const [visible, setVisible] = useState(false);
  /** Sidebar drawer expanded vs hidden (drawer toggle, not initial fade-in). */
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);
  const scrollTimeoutRef = useRef<number | null>(null);

  // Initial fade-in, 300ms after mount.
  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), 300);
    return () => window.clearTimeout(t);
  }, []);

  // Scroll-spy: pick the bottommost section whose top has crossed viewport midline.
  useEffect(() => {
    const update = (): void => {
      const mid = window.scrollY + window.innerHeight / 2;
      let next = SECTIONS[0].id;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SECTIONS[i].id);
        if (el && el.offsetTop <= mid) { next = SECTIONS[i].id; break; }
      }
      setActiveId(next);
    };
    const onScroll = (): void => {
      if (scrollTimeoutRef.current != null) window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = window.setTimeout(update, 100);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update(); // initial
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (scrollTimeoutRef.current != null) window.clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const handleNavClick = (id: string) => (e: React.MouseEvent): void => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      setActiveId(id);
      el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  };

  const sidebarClass = [
    styles.floatingSidebar,
    visible && drawerOpen ? styles.visible : '',
    !drawerOpen ? styles.hidden : '',
  ].filter(Boolean).join(' ');

  const toggleClass = [
    styles.sidebarToggle,
    visible ? styles.visible : '',
    !drawerOpen ? styles.menuHidden : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <button
        type="button"
        className={toggleClass}
        onClick={() => setDrawerOpen((v) => !v)}
        title={drawerOpen ? 'Hide Menu' : 'Show Menu'}
        aria-label="Toggle dashboard navigation"
        aria-expanded={drawerOpen}
      >
        {drawerOpen ? '‹' : '›'}
      </button>
      <nav id="floating-sidebar" className={sidebarClass} aria-label="Dashboard navigation">
        <div>
          <h4 className={styles.sidebarTitle}>Dashboard Overview</h4>
          <p className={styles.sidebarDate}>As of {formatLong(new Date())}</p>
          <ul className={styles.sidebarMenu}>
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className={`${styles.sidebarLink} ${activeId === s.id ? styles.active : ''}`}
                  onClick={handleNavClick(s.id)}
                  data-target={s.id}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </>
  );
}
